import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDismissMenu } from '../lib/useDismissMenu.ts';
import { useNavigate } from 'react-router-dom';
import { useBuilds, isActiveBuild, formatBuildAge } from '../lib/useBuilds.ts';
import { recallBuilds, forgetBuild } from '../lib/activeBuild.ts';
import { X } from '../lib/icons.tsx';
import { buildDestination } from '../lib/buildRoute.ts';
import AnchoredPop from './AnchoredPop.tsx';

/**
 * "2 building" in the top bar, opening a list of in-flight builds that each
 * link to their own `/build/:jobId`.
 *
 * This is what makes a backgrounded build a real thing rather than a lost one.
 * Before it, the only surface that could see a running build was the ⚙ drawer's
 * Builds panel — so walking away from the wizard meant the build carried on
 * with no way back to it short of waiting for the deck to appear on the
 * dashboard.
 *
 * Renders NOTHING when no build is in flight: an always-present "0 building"
 * chip is noise in a top bar that is mostly not about builds.
 */
export default function BuildsIndicator() {
  const { builds, maxConcurrent } = useBuilds();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * Whether this indicator is inside the app's bottom sheet.
   *
   * The two cases want opposite things. Floating, the panel has to be portalled
   * and placed against the viewport, because its list is unbounded — fourteen
   * builds ran it 39px off the bottom of a 560px-tall window. Inside the sheet
   * it must NOT float at all: `.app-sheet__body` is a clipping scroller, so a
   * panel hanging below its row opens straight into the clip (see base.css).
   *
   * Asked of the DOM rather than inferred from a breakpoint, because the CSS
   * rule that handles the sheet case is keyed on this exact ancestor.
   */
  const [inSheet, setInSheet] = useState(false);
  useLayoutEffect(() => {
    if (open) setInSheet(!!rootRef.current?.closest('.app-sheet'));
  }, [open]);

  // Click-away and Escape, so the panel never strands itself open over the page.
  // BOTH refs: the panel is portalled when floating, so it is not inside
  // rootRef and a click on one of its own rows would read as an outside click.
  // That two-ref case is exactly what useDismissMenu's array form is for, and
  // this file hand-rolled it while importing AnchoredPop from next door.
  const closePanel = useCallback(() => setOpen(false), []);
  useDismissMenu([rootRef, panelRef], closePanel, open);

  const active = (builds || []).filter(isActiveBuild);

  // Builds THIS browser started that have since gone terminal. The watch route
  // clears its own record when it sees `done`, so anything still remembered
  // and no longer running is a build that finished while the user was
  // somewhere else — the one case where nothing would otherwise tell them.
  // Scoped to this browser's own records deliberately: a colleague's build
  // completing is not news to announce here.
  const [mine, setMine] = useState<string[]>([]);
  useEffect(() => {
    setMine(recallBuilds('new-deck').map((r) => r.jobId));
  }, [builds]);
  const finished = (builds || []).filter((b) => !isActiveBuild(b) && mine.includes(b.id));

  function dismiss(id: string) {
    forgetBuild('new-deck', id);
    setMine((ids) => ids.filter((x) => x !== id));
  }

  const total = active.length + finished.length;
  useEffect(() => {
    if (!total) setOpen(false);
  }, [total]);

  const panelBody = (
    <>
      {active.length > 0 && (
        <p className="small muted builds-indicator__meta">
          {active.length} of {maxConcurrent || active.length} build slot
          {(maxConcurrent || active.length) === 1 ? '' : 's'} in use
        </p>
      )}
      <ul className="builds-indicator__list">
        {active.map((b) => (
          <li key={b.id}>
            <button
              type="button"
              className="builds-indicator__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate(buildDestination(b), { state: { slug: b.slug } });
              }}
            >
              <span className="builds-indicator__slug">{b.slug || b.id}</span>
              <span className="muted small">
                {b.engine}
                {b.phase ? ` · ${b.phase}` : ''}
                {typeof b.slides === 'number' ? ` · ${b.slides} slide${b.slides === 1 ? '' : 's'}` : ''}
                {` · ${formatBuildAge(b.startedAt)}`}
              </span>
            </button>
          </li>
        ))}
        {finished.map((b) => (
          <li key={b.id} className="builds-indicator__done">
            <button
              type="button"
              className="builds-indicator__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                dismiss(b.id);
                // A finished build opens its DECK; one that stopped short
                // opens its build page, where the retry lives.
                navigate(b.status === 'done' && b.slug ? `/editor/${b.slug}` : `/build/${b.id}`, {
                  state: { slug: b.slug },
                });
              }}
            >
              <span className="builds-indicator__slug">{b.slug || b.id}</span>
              <span className="muted small">
                {b.status === 'done'
                  ? `finished${typeof b.slides === 'number' ? ` · ${b.slides} slides` : ''} · open it`
                  : b.status === 'cancelled'
                    ? 'cancelled'
                    : 'stopped before finishing'}
              </span>
            </button>
            <button
              type="button"
              className="iconbtn builds-indicator__dismiss"
              onClick={() => dismiss(b.id)}
              aria-label="Dismiss"
              title="Dismiss"
            >
              <X />
            </button>
          </li>
        ))}
      </ul>
    </>
  );

  if (!total) return null;

  return (
    <div className="builds-indicator" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className={'btn btn--ghost btn--sm builds-indicator__button' + (active.length ? '' : ' is-ready')}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={
          active.length
            ? `${active.length} build${active.length === 1 ? '' : 's'} running${
                maxConcurrent ? ` of ${maxConcurrent} slots` : ''
              }`
            : `${finished.length} finished build${finished.length === 1 ? '' : 's'}`
        }
      >
        <span className="builds-indicator__dot" aria-hidden="true" />
        {active.length ? `${active.length} building` : `${finished.length} ready`}
      </button>
      {open &&
        (inSheet ? (
          <div className="builds-indicator__panel" role="menu" ref={panelRef}>
            {panelBody}
          </div>
        ) : (
          <AnchoredPop
            anchor={buttonRef}
            popRef={panelRef}
            align="right"
            gap={6}
            className="builds-indicator__panel"
            role="menu"
          >
            {panelBody}
          </AnchoredPop>
        ))}
    </div>
  );
}
